/**
 * Verification Queue Component
 * Displays and manages the queue of pending verification requests for admin
 */

import React, { useEffect, useState } from 'react';
import useAdminSocket from '../../hooks/useAdminSocket';

interface VerificationRequest {
    id: string;
    userId: string;
    userName: string;
    documentType: string;
    status: 'pending' | 'in_review' | 'approved' | 'rejected';
    submittedAt: string;
}

interface QueueUpdate {
    count: number;
    pendingVerifications: VerificationRequest[];
}

const VerificationQueue: React.FC = () => {
    const [queueData, setQueueData] = useState<QueueUpdate>({
        count: 0,
        pendingVerifications: []
    });

    const { connectionState, subscribeToQueueUpdates } = useAdminSocket('admin-1');

    useEffect(() => {
        if (!connectionState.isConnected) return;

        // Subscribe to queue updates
        const unsubscribe = subscribeToQueueUpdates((data: QueueUpdate) => {
            console.log('Received queue update:', data);
            setQueueData(data);
        });

        return unsubscribe;
    }, [connectionState.isConnected, subscribeToQueueUpdates]);

    if (!connectionState.isConnected) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Connecting to verification queue...</p>
                </div>
            </div>
        );
    }

    if (connectionState.error) {
        return (
            <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="text-lg font-semibold text-red-800 mb-2">Connection Error</h3>
                <p className="text-red-700">{connectionState.error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Verification Queue</h2>
                        <p className="text-gray-600 mt-1">
                            Manage pending verification requests
                        </p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                            {queueData.count} pending
                        </div>
                        <div className={`w-3 h-3 rounded-full ${connectionState.isConnected ? 'bg-green-500' : 'bg-red-500'
                            }`} title={connectionState.isConnected ? 'Connected' : 'Disconnected'} />
                    </div>
                </div>

                <div className="space-y-4">
                    {queueData.pendingVerifications.length === 0 ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No pending verifications</h3>
                            <p className="text-gray-500">All verification requests have been processed.</p>
                        </div>
                    ) : (
                        queueData.pendingVerifications.map((request) => (
                            <div
                                key={request.id}
                                className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-3">
                                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-blue-600 font-medium text-sm">
                                                    {request.userName.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-gray-900">{request.userName}</h4>
                                                <p className="text-sm text-gray-500">
                                                    {request.documentType} • {new Date(request.submittedAt).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                request.status === 'in_review' ? 'bg-blue-100 text-blue-800' :
                                                    request.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                        'bg-red-100 text-red-800'
                                            }`}>
                                            {request.status.replace('_', ' ')}
                                        </span>
                                        <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                                            Review
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerificationQueue;